export * from './membership-cardholder.type';
export * from './membership-details.type';
export * from './constants';

import { LooseAutocomplete, SalesChannel, SalesChannelType, Timestamp, TODO } from '../core';
import { FormDetail } from '../forms';
import { MembershipLevelsForLifecycleActionForms, SalesChannelFormRestrictions } from './membership-details.type';

/**
 * Form with additional cart context (membership name, item type)
 */
export interface CartMembershipOrderForm extends FormDetail {
  membershipName: string;
  itemType: MembershipItemType;
  membershipOfferingId?: string;
  formPreferences: SalesChannelFormRestrictions[];
  membershipLevelsInfo: MembershipLevelsForLifecycleActionForms[];
  displayFormForGiftPurchase?: boolean;
  displayFormForStandardPurchase?: boolean;
  displayFormForAutoRenewPurchase?: boolean;
}

/**
 * Type guard to identify a membership order form by checking for the
 * membership-specific `membershipName` property.
 */
export function isMembershipOrderForm(form: object): form is CartMembershipOrderForm {
  return 'membershipName' in form;
}

import { MembershipCardholder } from './membership-cardholder.type';

export type AutorenewalType = 'none' | 'optional' | 'required';

export interface Membership {
  itemId: string;
  quantity: number;
  unitPrice: string;
  retailUnitPrice: string;
  amount: string;
  itemType: MembershipItemType;
  membershipInfo: MembershipInfo;
  admissionType: string;
  isRetail: boolean;
  ignoreEntitlements: boolean;
  level: MembershipLevel;
  couponCodes: string[];
  errors: string[]; // TODO: UI only?
  messages: string[]; // TODO: UI only?
}

/**
 * All possible membership item types — matches the values the cart API uses for
 * membership line-item `itemType` and lifecycle-action form routing.
 */
export type MembershipItemType =
  | 'MembershipPurchase'
  | 'MembershipRenewal'
  | 'MembershipUpgrade'
  | 'MembershipDowngrade'
  | 'MembershipCancel'
  | 'MembershipPostTermRenewal'
  | 'MembershipRejoin'
  | 'MembershipRenewalUpgrade'
  | 'MembershipPostTermRenewalUpgrade'
  | 'MembershipRejoinUpgrade'
  | 'MembershipPostTermRenewalDowngrade'
  | 'MembershipRejoinDowngrade'
  | 'MembershipRenewalDowngrade'
  | 'MembershipPurchaseConversion'
  | 'MembershipReplacement'
  | 'MembershipOfferingChange';

/**
 * Membership purchase type - represents the type of membership transaction
 */
export type MembershipPurchaseType =
  | 'MembershipPurchase'
  | 'MembershipUpgrade'
  | 'MembershipRenewal'
  | 'MembershipPostTermRenewal'
  | 'MembershipRejoin'
  | 'MembershipRenewalUpgrade'
  | 'MembershipPostTermRenewalUpgrade'
  | 'MembershipRejoinUpgrade'
  | 'MembershipRenewalDowngrade'
  | 'MembershipPostTermRenewalDowngrade'
  | 'MembershipRejoinDowngrade';

/**
 * Rule category - represents the lifecycle action category for membership forms
 */
export type RuleCategory =
  | 'STANDARD_NEW'
  | 'STANDARD_RENEW'
  | 'UPGRADE_RENEW'
  | 'DOWNGRADE_RENEW'
  | 'STANDARD_UPGRADE'
  | 'EARLY_TERM_UPGRADE'
  | 'MID_TERM_UPGRADE'
  | 'LATE_TERM_UPGRADE'
  | 'STANDARD_DOWNGRADE'
  | 'EARLY_TERM_DOWNGRADE'
  | 'MID_TERM_DOWNGRADE'
  | 'LATE_TERM_DOWNGRADE'
  | 'AFTER_TERM_RENEW'
  | 'AFTER_TERM_UPGRADE'
  | 'AFTER_TERM_DOWNGRADE'
  | 'REJOIN_RENEW'
  | 'REJOIN_UPGRADE'
  | 'REJOIN_DOWNGRADE'
  | 'STANDARD_CANCELLATION'
  | 'EARLY_TERM_CANCELLATION'
  | 'LATE_TERM_CANCELLATION';

export interface MembershipListData {
  id: string;
  name: string;
  rules: TODO;
  offerings: TODO;
  state: string;
  membershipLevelGroup: string;
  displayOrder: number;
  auxiliary: boolean;
}

export interface MembershipInfo {
  membershipCategoryId: string;
  membershipCategoryName: string;
  membershipOfferingId: string;
  membershipOfferingName: string;
  pricePointId: string;
  pricePointName: string;
  waiveBenefits: boolean;
  isGift: boolean;
  notifyGiftRecipient: boolean;
  donationAmount: string;
  markDonationAsAnonymous: boolean;
  cancellationRefundAmount: string;
  auxiliary: boolean;
  autorenewalType: AutorenewalType;
  isAutorenew?: boolean;
  membershipCards: MembershipCardholder[];
}

export interface MembershipRules {
  numberOfCardHolders: number;
  canGift: boolean;
  startBeginningOfMonth: boolean;
  expireEndOfMonth: boolean;
}

export interface MembershipLevel {
  id: string;
  name: string;
  description: string;
  shortDescription: string;
  rules: MembershipRules;
  benefits: MembershipBenefit[];
  offerings: MembershipLevelOffering[];
}

export interface MembershipBenefit {
  description: string[];
}

export interface MembershipLevelOffering {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  duration: number;
  benefits: MembershipBenefit[];
  rules: MembershipRules;
  priceLists: MembershipPriceList[];
  actions: MembershipAction[];
  autorenewalType: AutorenewalType;
  externalMembershipPriceRequired: boolean;
  displayName: string;
  term: boolean;
}

export interface MembershipPriceList {
  prices: MembershipPrice[];
  startTime: Timestamp;
}

export interface MembershipPrice {
  personType: {
    id: string;
    name: string;
    description: string;
    active: boolean;
    type: string;
    displayOrder: number;
  };
  price: string;
  basePrice?: string | null;
  primePrice: string;
  salesChannels: SalesChannel[];
  displayOrder: number;
  discountedPrice: string;
}

export type MembershipActionType = LooseAutocomplete<
  'NEW_MEMBERSHIPS' | 'RENEW' | 'UPGRADE' | 'DOWNGRADE' | 'AFTER_TERM_RENEW' | 'REJOIN' | 'CANCELLATION'
>;

export interface MembershipAction {
  action: MembershipActionType;
  ruleIds: string[];
  salesChannels: SalesChannel[];
  confirmationEmailRestrictions: {
    saleChannel: SalesChannelType;
    canSendConfirmationEmail: true;
  };
  printMembershipCards: boolean;
  includeDigitalMemberCardsInConfirmationEmail: boolean;
  confirmationMessage: string;
}
